<div class="corporate-reporting-container">
  <div class="corporate-reporting-header">
    <h2>{{ 'Corporate Reporting' | translate }}</h2>
    <p class="subtitle">{{ 'Cross-region, PII-redacted reporting from the corporate store.' | translate }}</p>
  </div>

  <div class="corporate-reporting-filter">
    <mat-form-field appearance="outline">
      <mat-label>{{ 'Region' | translate }}</mat-label>
      <mat-select [(ngModel)]="selectedRegionCode" (selectionChange)="onRegionChange()">
        <mat-option [value]="''">{{ 'All Regions' | translate }}</mat-option>
        @for (region of regionOptions; track region) {
          <mat-option [value]="region.code">{{ region.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
  </div>

  <section class="corporate-reporting-section">
    <h3>{{ 'Cross-Region Samples' | translate }}</h3>
    <div class="table-scroll">
      <table mat-table [dataSource]="samples" [trackBy]="trackById">
        <ng-container matColumnDef="regionLabel">
          <th mat-header-cell *matHeaderCellDef>{{ 'Region' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.regionLabel }}</td>
        </ng-container>
        <ng-container matColumnDef="sampleNumber">
          <th mat-header-cell *matHeaderCellDef>{{ 'Sample Number' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.sampleNumber | nullDisplay }}</td>
        </ng-container>
        <ng-container matColumnDef="typeLabel">
          <th mat-header-cell *matHeaderCellDef>{{ 'Type' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.typeLabel }}</td>
        </ng-container>
        <ng-container matColumnDef="statusLabel">
          <th mat-header-cell *matHeaderCellDef>{{ 'Status' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.statusLabel }}</td>
        </ng-container>
        <ng-container matColumnDef="approvedDate">
          <th mat-header-cell *matHeaderCellDef>{{ 'Approved On' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.approvedDate ? (row.approvedDate | dateFormat) : '-' }}</td>
        </ng-container>
        <ng-container matColumnDef="twa">
          <th mat-header-cell *matHeaderCellDef>{{ 'TWA' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.twa | nullDisplay }}</td>
        </ng-container>
        <ng-container matColumnDef="oel">
          <th mat-header-cell *matHeaderCellDef>{{ 'OEL' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.oel | nullDisplay }}</td>
        </ng-container>
        <ng-container matColumnDef="dosePercentage">
          <th mat-header-cell *matHeaderCellDef>{{ '% of OEL' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.dosePercentage | nullDisplay }}</td>
        </ng-container>
        <ng-container matColumnDef="censorLabel">
          <th mat-header-cell *matHeaderCellDef>{{ 'Censor' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.censorLabel }}</td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="sampleColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: sampleColumns"></tr>
      </table>
    </div>
    @if (!samplesLoading && samples.length === 0) {
      <p class="empty-state">{{ 'No records found' | translate }}</p>
    }
    <mat-paginator [length]="samplesCount" [pageSize]="pageSize" [pageIndex]="samplesPageIndex"
    [hidePageSize]="true" (page)="onSamplesPage($event)"></mat-paginator>
  </section>

  <section class="corporate-reporting-section">
    <h3>{{ 'Risk Rollups' | translate }}</h3>
    <div class="table-scroll">
      <table mat-table [dataSource]="rollups" [trackBy]="trackById">
        <ng-container matColumnDef="regionLabel">
          <th mat-header-cell *matHeaderCellDef>{{ 'Region' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.regionLabel }}</td>
        </ng-container>
        <ng-container matColumnDef="numberOfWorkers">
          <th mat-header-cell *matHeaderCellDef>{{ 'Employees' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.numberOfWorkers | nullDisplay }}</td>
        </ng-container>
        <ng-container matColumnDef="numberOfContractors">
          <th mat-header-cell *matHeaderCellDef>{{ 'Contractors' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.numberOfContractors | nullDisplay }}</td>
        </ng-container>
        <ng-container matColumnDef="quantitativeGeometricMean">
          <th mat-header-cell *matHeaderCellDef>{{ 'GM' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.quantitativeGeometricMean | nullDisplay }}</td>
        </ng-container>
        <ng-container matColumnDef="quantitativeGSD">
          <th mat-header-cell *matHeaderCellDef>{{ 'GSD' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.quantitativeGSD | nullDisplay }}</td>
        </ng-container>
        <ng-container matColumnDef="quantitative95thPercentile">
          <th mat-header-cell *matHeaderCellDef>{{ '95th Percentile' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.quantitative95thPercentile | nullDisplay }}</td>
        </ng-container>
        <ng-container matColumnDef="quantitativeResultPercentOfOEL">
          <th mat-header-cell *matHeaderCellDef>{{ '% of OEL' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.quantitativeResultPercentOfOEL | nullDisplay }}</td>
        </ng-container>
        <ng-container matColumnDef="exposureRiskLabel">
          <th mat-header-cell *matHeaderCellDef>{{ 'Exposure Risk' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.exposureRiskLabel }}</td>
        </ng-container>
        <ng-container matColumnDef="compliantLabel">
          <th mat-header-cell *matHeaderCellDef>{{ 'Compliant' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.compliantLabel }}</td>
        </ng-container>
        <ng-container matColumnDef="assessmentStatusLabel">
          <th mat-header-cell *matHeaderCellDef>{{ 'Assessment Status' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.assessmentStatusLabel }}</td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="rollupColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: rollupColumns"></tr>
      </table>
    </div>
    @if (!rollupsLoading && rollups.length === 0) {
      <p class="empty-state">{{ 'No records found' | translate }}</p>
    }
    <mat-paginator [length]="rollupsCount" [pageSize]="pageSize" [pageIndex]="rollupsPageIndex"
    [hidePageSize]="true" (page)="onRollupsPage($event)"></mat-paginator>
  </section>
</div>
